import { useEffect, useState } from "react";
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, FormSelect, FormTextarea } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import { useCreateMembership, useMembershipTiers } from "@/api/hooks/useMemberships";
import { formatQuota } from "./hourMath";
import { ContactPicker } from "./ContactPicker";

interface MemberFormDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (membershipId: string) => void;
}

/** Create-membership form: contact picker + tier select. New memberships
 * always start `pending` (server-enforced) — activation is a separate,
 * explicit action on the member detail page, since it's the moment
 * billing/the 3-month clock actually starts. */
export function MemberFormDialog({ open, onClose, onCreated }: MemberFormDialogProps) {
  const { data: tiers } = useMembershipTiers();
  const createMembership = useCreateMembership();

  const [contactId, setContactId] = useState("");
  const [contactName, setContactName] = useState("");
  const [tierId, setTierId] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setContactId("");
      setContactName("");
      setTierId("");
      setNotes("");
    }
  }, [open]);

  const canSubmit = !!contactId && !!tierId && !createMembership.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      const membership = await createMembership.mutateAsync({
        contact_id: contactId,
        tier_id: tierId,
        notes: notes.trim() || null,
      });
      toastSuccess("Member added");
      onCreated?.(membership.id);
      onClose();
    } catch (err) {
      toastError("Couldn't add member", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Add member">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>Add member</DialogHeader>
        <DialogBody className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Contact</label>
            <ContactPicker value={contactId} onChange={(id, name) => { setContactId(id); setContactName(name); }} />
            {contactName && !contactId && (
              <p className="mt-1 text-xs text-danger">Select a contact from the list.</p>
            )}
          </div>

          <FormSelect label="Membership tier" value={tierId} onChange={(e) => setTierId(e.target.value)}>
            <option value="">Select a tier…</option>
            {(tiers ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {centsToUsd(t.price_cents)}/mo ({formatQuota(t.weekly_hour_quota)}
                {t.storage ? ", storage" : ""})
              </option>
            ))}
          </FormSelect>

          <FormTextarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createMembership.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit} data-testid="submit-member-form">
            {createMembership.isPending ? "Adding…" : "Add member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
